import React, { useState } from 'react';
import { Shield, Copy, Check, X, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onStatusChange: () => void;
}

export const TwoFASetupModal: React.FC<Props> = ({ isOpen, onClose, onStatusChange }) => {
  const [step, setStep] = useState<'start' | 'scan' | 'verify' | 'done'>('start');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const getToken = async () => (await supabase.auth.getSession()).data.session?.access_token;

  const handleSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getToken();
      const res = await fetch('/api/admin/2fa/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setRecoveryCodes(data.recoveryCodes);
      setStep('scan');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const token = await getToken();
      const res = await fetch('/api/admin/2fa/verify-enable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setStep('done');
      onStatusChange();
      // Auto-close after brief success display
      setTimeout(() => onClose(), 1500);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, backdropFilter: 'blur(4px)',
    }} onClick={onClose}>
      <div style={{
        background: 'var(--surface)', borderRadius: 16, width: '100%', maxWidth: 460,
        padding: 32, boxShadow: '0 24px 48px rgba(0,0,0,0.2)', border: '1px solid var(--border)',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Shield size={18} color="#fff" />
            </div>
            <h2 style={{ margin: 0 }}>2FA einrichten</h2>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
        </div>

        {/* Step: Start */}
        {step === 'start' && (
          <div>
            <p style={{ color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
              Schütze deinen Admin-Zugang mit einem zweiten Faktor. Du benötigst eine Authenticator-App
              wie <strong>Google Authenticator</strong>, <strong>Authy</strong> oder <strong>Microsoft Authenticator</strong>.
            </p>
            <button className="btn btn-primary w-full" onClick={handleSetup} disabled={loading}
              style={{ height: 42 }}>
              {loading ? <Loader2 style={{ animation: 'spin 1s linear infinite' }} /> : '2FA aktivieren'}
            </button>
          </div>
        )}

        {/* Step: Scan QR */}
        {step === 'scan' && (
          <div>
            <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
              Scanne den QR-Code mit deiner Authenticator-App:
            </p>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <img src={qrCode} alt="TOTP QR Code" style={{ width: 200, height: 200, borderRadius: 8 }} />
            </div>
            <div style={{
              background: 'var(--background)', borderRadius: 8, padding: '10px 12px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 16, border: '1px solid var(--border)',
            }}>
              <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{secret}</code>
              <button className="btn btn-ghost btn-sm" onClick={copySecret} title="Kopieren">
                {copied ? <Check size={14} color="#22C55E" /> : <Copy size={14} />}
              </button>
            </div>

            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
              <strong>Recovery Codes</strong> — speichere diese sicher ab! Sie können nur einmal verwendet werden:
            </p>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6,
              background: 'var(--background)', borderRadius: 8, padding: 12,
              border: '1px solid var(--border)', marginBottom: 20,
            }}>
              {recoveryCodes.map((c, i) => (
                <code key={i} style={{ fontSize: 13, fontWeight: 600, padding: '2px 0' }}>{c}</code>
              ))}
            </div>

            <form onSubmit={handleVerifyEnable}>
              <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>
                Bestätigungscode eingeben:
              </label>
              <input
                type="text" className="input" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000" maxLength={6} autoComplete="one-time-code"
                style={{ textAlign: 'center', fontSize: 20, letterSpacing: 6, fontWeight: 700, marginBottom: 12 }}
              />
              {error && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{error}</div>}
              <button className="btn btn-primary w-full" disabled={loading || code.length < 6}
                style={{ height: 42 }}>
                {loading ? <Loader2 style={{ animation: 'spin 1s linear infinite' }} /> : 'Aktivieren'}
              </button>
            </form>
          </div>
        )}

        {/* Step: Done */}
        {step === 'done' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'rgba(34,197,94,0.1)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Check size={32} color="#22C55E" />
            </div>
            <h3>2FA erfolgreich aktiviert!</h3>
            <p className="text-muted" style={{ marginBottom: 20 }}>
              Ab jetzt wird bei jedem Login ein 6-stelliger Code abgefragt.
            </p>
            <button className="btn btn-primary" onClick={onClose}>Fertig</button>
          </div>
        )}

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};
